import { SubmitHandler, useForm } from 'react-hook-form'
import zod from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Form, ButtonLoader, Button } from '@wellesley/ui'
import { FC } from 'react'

import { usePostApiV1AccountsEmailChangeRequestMutation } from '../../../../authApi'
import { useDispatch } from 'react-redux'
import { authSlice } from '../../../../authSlice'
import CommonFormField from 'components/form/CommonFormField'
import {
  emailValidator,
  passwordValidator,
  emailMaxLength,
  maxPwdLength,
} from '@wellesley/forms'
import { TooManyAttempt } from '../../../TooManyAttempt'

const formSchema = zod.object({
  email: emailValidator,
  password: passwordValidator,
})

export type ChangeEmailFormInput = zod.TypeOf<typeof formSchema>

type ChangeEmailFormProps = {
  onCancel: () => void
}

export const ChangeEmailForm: FC<ChangeEmailFormProps> = ({ onCancel }) => {
  const dispatch = useDispatch()
  const { setNewEmail, setChangeEmailStep } = authSlice.actions

  const form = useForm<ChangeEmailFormInput>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })
  const { control, handleSubmit } = form

  const [requestChange, { isError, error, isLoading }] =
    usePostApiV1AccountsEmailChangeRequestMutation()

  const onSubmit: SubmitHandler<ChangeEmailFormInput> = async (values) => {
    await requestChange({
      emailChangeRequest: {
        newEmail: values.email,
        password: values.password,
      },
    }).unwrap()
    dispatch(setNewEmail(values.email))
    dispatch(setChangeEmailStep('passCode'))
  }

  return (
    <Form {...form}>
      <form
        className="flex flex-col"
        onSubmit={(...args) => void handleSubmit(onSubmit)(...args)}
      >
        <div className="border-b border-gray-200 p-m">
          <h2 className="text-center text-lg font-semibold text-gray-700">
            Update email address
          </h2>
        </div>
        <div className="flex flex-col gap-m px-m py-m">
          <TooManyAttempt
            message="The attempt was unsuccessful, please try again later"
            isError={isError}
            error={error}
          />
          <CommonFormField
            type="email"
            control={control}
            name="email"
            displayName="New email address"
            maxChars={emailMaxLength}
            className="gap-xxs"
            inputClassName="rounded-m border border-gray-300 px-s py-xs text-base text-gray-900 outline-offset-[-1px]"
          />
          <CommonFormField
            type="password"
            control={control}
            name="password"
            displayName="Current password"
            maxChars={maxPwdLength}
            className="gap-xxs"
            inputClassName="rounded-m border border-gray-300 px-s py-xs text-base text-gray-900 outline-offset-[-1px]"
          />
        </div>
        <div className="flex items-center justify-end gap-xs px-m pb-m">
          <Button
            type="button"
            variant="secondary"
            size="m"
            className="flex-1"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <ButtonLoader
            type="submit"
            size="m"
            variant="primary"
            isLoading={isLoading}
            className="flex-1"
          >
            Next
          </ButtonLoader>
        </div>
      </form>
    </Form>
  )
}
